import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ComposedChart, ScatterChart, Scatter, Treemap, FunnelChart, Funnel, LabelList
} from 'recharts';
import {
  TrendingUp, TrendingDown, DollarSign, Users, ShoppingCart, Package,
  AlertTriangle, BarChart3, PieChart as PieChartIcon, Activity,
  ArrowUpRight, ArrowDownRight, Calendar, Filter, Download, RefreshCw,
  CheckCircle
} from 'lucide-react';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7300'];

const Advanced360Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('revenue');
  const [dateRange, setDateRange] = useState('30');
  const [refreshKey, setRefreshKey] = useState(0);

  // Data states
  const [revenueForecast, setRevenueForecast] = useState(null);
  const [customerSegments, setCustomerSegments] = useState([]);
  const [cohortRetention, setCohortRetention] = useState([]);
  const [funnelData, setFunnelData] = useState([]);
  const [productAffinity, setProductAffinity] = useState([]);
  const [marketingAttribution, setMarketingAttribution] = useState([]);
  const [aovAnalysis, setAovAnalysis] = useState(null);
  const [salesVelocity, setSalesVelocity] = useState([]);
  const [clvData, setClvData] = useState(null);
  const [anomalies, setAnomalies] = useState([]);
  const [customerJourneys, setCustomerJourneys] = useState([]);
  const [seasonality, setSeasonality] = useState(null);

  const baseUrl = API_BASE_URL;
  const token = localStorage.getItem('adminToken');

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Fetch all analytics data in parallel
      const [
        forecastRes, segmentsRes, cohortRes, funnelRes, affinityRes,
        attributionRes, aovRes, velocityRes, clvRes, anomaliesRes,
        journeysRes, seasonalityRes
      ] = await Promise.all([
        fetch(`${baseUrl}/api/admin/advanced-analytics/revenue-forecast?days=${dateRange}`, { headers }),
        fetch(`${baseUrl}/api/admin/advanced-analytics/customer-segments`, { headers }),
        fetch(`${baseUrl}/api/admin/advanced-analytics/cohort-retention?months=6`, { headers }),
        fetch(`${baseUrl}/api/admin/advanced-analytics/funnel-analysis?days=${dateRange}`, { headers }),
        fetch(`${baseUrl}/api/admin/advanced-analytics/product-affinity?limit=20`, { headers }),
        fetch(`${baseUrl}/api/admin/advanced-analytics/marketing-attribution?days=${dateRange}`, { headers }),
        fetch(`${baseUrl}/api/admin/advanced-analytics/aov-analysis?days=${dateRange}`, { headers }),
        fetch(`${baseUrl}/api/admin/advanced-analytics/sales-velocity?days=${dateRange}`, { headers }),
        fetch(`${baseUrl}/api/admin/advanced-analytics/clv-prediction`, { headers }),
        fetch(`${baseUrl}/api/admin/advanced-analytics/anomaly-detection?unresolved=true`, { headers }),
        fetch(`${baseUrl}/api/admin/advanced-analytics/customer-journey?days=7`, { headers }),
        fetch(`${baseUrl}/api/admin/advanced-analytics/seasonality?days=365`, { headers })
      ]);

      if (forecastRes.ok) setRevenueForecast(await forecastRes.json());
      if (segmentsRes.ok) setCustomerSegments((await segmentsRes.json()).segments || []);
      if (cohortRes.ok) setCohortRetention((await cohortRes.json()).cohorts || []);
      if (funnelRes.ok) setFunnelData((await funnelRes.json()).funnel || []);
      if (affinityRes.ok) setProductAffinity((await affinityRes.json()).affinities || []);
      if (attributionRes.ok) setMarketingAttribution((await attributionRes.json()).attribution || []);
      if (aovRes.ok) setAovAnalysis(await aovRes.json());
      if (velocityRes.ok) setSalesVelocity((await velocityRes.json()).products || []);
      if (clvRes.ok) setClvData(await clvRes.json());
      if (anomaliesRes.ok) setAnomalies((await anomaliesRes.json()).anomalies || []);
      if (journeysRes.ok) setCustomerJourneys((await journeysRes.json()).journeys || []);
      if (seasonalityRes.ok) setSeasonality(await seasonalityRes.json());
    } catch (error) {
      console.error('Error fetching analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dateRange, refreshKey]);

  const renderRevenueIntelligence = () => {
    if (!revenueForecast) return <div className="text-center py-8">No revenue data available</div>;

    const { historical = [], forecast = [], confidence = 0, trend = {} } = revenueForecast;
    const combinedData = [
      ...historical.map(d => ({ ...d, type: 'historical' })),
      ...forecast.map(d => ({ ...d, type: 'forecast' }))
    ];

    return (
      <div className="space-y-6">
        {/* Revenue Forecast Chart */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-800">Revenue Forecast</h3>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Confidence: {Math.round(confidence * 100)}%</span>
              {trend.slope > 0 ? (
                <TrendingUp className="h-5 w-5 text-green-500" />
              ) : (
                <TrendingDown className="h-5 w-5 text-red-500" />
              )}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={400}>
            <ComposedChart data={combinedData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip formatter={(value) => `KSh ${value.toLocaleString()}`} />
              <Legend />
              <Area
                type="monotone"
                dataKey="lowerBound"
                fill="#8884d8"
                fillOpacity={0.1}
                stroke="none"
                name="Lower Bound"
              />
              <Area
                type="monotone"
                dataKey="upperBound"
                fill="#8884d8"
                fillOpacity={0.1}
                stroke="none"
                name="Upper Bound"
              />
              <Line
                type="monotone"
                dataKey="revenue"
                stroke="#8884d8"
                strokeWidth={2}
                name="Revenue"
                dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* AOV Analysis */}
        {aovAnalysis && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">AOV by Segment</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={aovAnalysis.bySegment || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="segment" />
                  <YAxis />
                  <Tooltip formatter={(value) => `KSh ${value.toLocaleString()}`} />
                  <Bar dataKey="aov" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">AOV Trends</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={aovAnalysis.trends || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip formatter={(value) => `KSh ${value.toLocaleString()}`} />
                  <Line type="monotone" dataKey="aov" stroke="#8884d8" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Sales Velocity */}
        {salesVelocity.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Product Sales Velocity</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2">Product</th>
                    <th className="text-right p-2">Velocity (sales/day)</th>
                    <th className="text-right p-2">Total Sold</th>
                    <th className="text-right p-2">Days to Sell Out</th>
                  </tr>
                </thead>
                <tbody>
                  {salesVelocity.slice(0, 10).map((product) => (
                    <tr key={product.productId} className="border-b hover:bg-gray-50">
                      <td className="p-2">{product.name}</td>
                      <td className="text-right p-2">{product.velocity.toFixed(2)}</td>
                      <td className="text-right p-2">{product.totalSold}</td>
                      <td className="text-right p-2">
                        {product.daysToSellOut ? `${product.daysToSellOut} days` : 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderCustomerJourney = () => {
    return (
      <div className="space-y-6">
        {/* Funnel Visualization */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Conversion Funnel</h3>
          {funnelData.length > 0 ? (
            <div className="space-y-4">
              {funnelData.map((stage, index) => {
                const prevStage = funnelData[index - 1];
                const dropOffRate = prevStage
                  ? ((prevStage.uniqueSessions - stage.uniqueSessions) / prevStage.uniqueSessions) * 100
                  : 0;
                const width = (stage.conversionRate || 0) / 100 * 100;

                return (
                  <div key={stage.stage} className="relative">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-gray-700 capitalize">{stage.stage.replace('_', ' ')}</span>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-gray-600">{stage.uniqueSessions.toLocaleString()} sessions</span>
                        <span className="text-blue-600">{stage.conversionRate.toFixed(1)}% conversion</span>
                        {dropOffRate > 0 && (
                          <span className="text-red-600">{dropOffRate.toFixed(1)}% drop-off</span>
                        )}
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-8 relative overflow-hidden">
                      <div
                        className="bg-gradient-to-r from-blue-500 to-purple-500 h-8 rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                        style={{ width: `${width}%` }}
                      >
                        {width > 10 && (
                          <span className="text-white text-xs font-semibold">
                            {stage.uniqueSessions.toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">No funnel data available</div>
          )}
        </div>

        {/* Customer Segments */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Customer Segments</h3>
          {customerSegments.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {customerSegments.map((segment) => (
                <div key={segment.segmentName} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                  <h4 className="font-semibold text-gray-800 capitalize mb-2">
                    {segment.segmentName.replace('_', ' ')}
                  </h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Customers:</span>
                      <span className="font-semibold">{segment.totalCustomers.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Revenue:</span>
                      <span className="font-semibold">KSh {segment.totalRevenue.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Avg AOV:</span>
                      <span className="font-semibold">KSh {segment.avgOrderValue.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">No segment data available</div>
          )}
        </div>

        {/* Cohort Retention */}
        {cohortRetention.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Cohort Retention Analysis</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2">Cohort</th>
                    <th className="text-right p-2">Size</th>
                    {cohortRetention[0]?.retention.map((_, i) => (
                      <th key={i} className="text-right p-2">Month {i}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {cohortRetention.map((cohort) => (
                    <tr key={cohort.cohort} className="border-b hover:bg-gray-50">
                      <td className="p-2 font-semibold">{cohort.cohort}</td>
                      <td className="text-right p-2">{cohort.size}</td>
                      {cohort.retention.map((ret, i) => (
                        <td key={i} className="text-right p-2">
                          {ret.retentionRate.toFixed(1)}%
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderMarketingPerformance = () => {
    return (
      <div className="space-y-6">
        {/* Marketing Attribution */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Marketing Channel Attribution</h3>
          {marketingAttribution.length > 0 ? (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={marketingAttribution}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="channel" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="sessions" fill="#8884d8" name="Sessions" />
                <Bar dataKey="conversions" fill="#82ca9d" name="Conversions" />
                <Bar dataKey="revenue" fill="#ffc658" name="Revenue (KSh)" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-8 text-gray-500">No attribution data available</div>
          )}
        </div>

        {/* Channel Performance Table */}
        {marketingAttribution.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Channel Performance</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2">Channel</th>
                    <th className="text-right p-2">Sessions</th>
                    <th className="text-right p-2">Conversions</th>
                    <th className="text-right p-2">Conversion Rate</th>
                    <th className="text-right p-2">Revenue</th>
                    <th className="text-right p-2">Avg Order Value</th>
                  </tr>
                </thead>
                <tbody>
                  {marketingAttribution.map((channel) => (
                    <tr key={channel.channel} className="border-b hover:bg-gray-50">
                      <td className="p-2 font-semibold capitalize">{channel.channel}</td>
                      <td className="text-right p-2">{channel.sessions.toLocaleString()}</td>
                      <td className="text-right p-2">{channel.conversions.toLocaleString()}</td>
                      <td className="text-right p-2">{channel.conversionRate.toFixed(2)}%</td>
                      <td className="text-right p-2">KSh {channel.revenue.toLocaleString()}</td>
                      <td className="text-right p-2">KSh {channel.avgOrderValue.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderProductPerformance = () => {
    return (
      <div className="space-y-6">
        {/* Product Affinity */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Frequently Bought Together</h3>
          {productAffinity.length > 0 ? (
            <div className="space-y-4">
              {productAffinity.slice(0, 10).map((affinity, index) => (
                <div key={index} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-semibold">{affinity.product1?.name || 'Product 1'}</span>
                        <span className="text-gray-400">+</span>
                        <span className="font-semibold">{affinity.product2?.name || 'Product 2'}</span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <span>Bought together: {affinity.coOccurrence} times</span>
                        <span>Confidence: {(affinity.confidence * 100).toFixed(1)}%</span>
                        {affinity.lift && <span>Lift: {affinity.lift.toFixed(2)}</span>}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">No affinity data available</div>
          )}
        </div>

        {/* Sales Velocity Chart */}
        {salesVelocity.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Top Products by Sales Velocity</h3>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={salesVelocity.slice(0, 10)}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="velocity" fill="#8884d8" name="Sales per Day" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    );
  };

  const renderOperationalEfficiency = () => {
    return (
      <div className="space-y-6">
        {/* Anomaly Detection */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-800">Anomaly Detection</h3>
            {anomalies.length > 0 && (
              <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-semibold">
                {anomalies.length} Anomalies Detected
              </span>
            )}
          </div>
          {anomalies.length > 0 ? (
            <div className="space-y-3">
              {anomalies.slice(0, 10).map((anomaly) => (
                <div
                  key={anomaly.id}
                  className={`border-l-4 p-4 rounded ${
                    anomaly.severity === 'critical' ? 'border-red-500 bg-red-50' :
                    anomaly.severity === 'high' ? 'border-orange-500 bg-orange-50' :
                    anomaly.severity === 'medium' ? 'border-yellow-500 bg-yellow-50' :
                    'border-blue-500 bg-blue-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-gray-800 capitalize">{anomaly.metricType}</h4>
                      <p className="text-sm text-gray-600">
                        Expected: {anomaly.expectedValue.toLocaleString()} | 
                        Actual: {anomaly.metricValue.toLocaleString()} | 
                        Deviation: {anomaly.deviation.toFixed(2)}σ
                      </p>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-semibold capitalize ${
                      anomaly.severity === 'critical' ? 'bg-red-200 text-red-800' :
                      anomaly.severity === 'high' ? 'bg-orange-200 text-orange-800' :
                      anomaly.severity === 'medium' ? 'bg-yellow-200 text-yellow-800' :
                      'bg-blue-200 text-blue-800'
                    }`}>
                      {anomaly.severity}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Detected: {new Date(anomaly.detectedAt).toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-green-600">
              <CheckCircle className="h-12 w-12 mx-auto mb-2" />
              <p>No anomalies detected. All metrics are within expected ranges.</p>
            </div>
          )}
        </div>

        {/* Seasonality Analysis */}
        {seasonality && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Day of Week Trends</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={seasonality.dayOfWeekTrends || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <Tooltip formatter={(value) => `KSh ${value.toLocaleString()}`} />
                  <Bar dataKey="avgRevenue" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Monthly Trends</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={seasonality.monthlyTrends || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(value) => `KSh ${value.toLocaleString()}`} />
                  <Bar dataKey="avgRevenue" fill="#82ca9d" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* CLV Analysis */}
        {clvData && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Customer Lifetime Value Analysis</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {Object.entries(clvData.segments || {}).map(([tier, data]) => (
                <div key={tier} className="border rounded-lg p-4">
                  <h4 className="font-semibold text-gray-800 capitalize mb-2">{tier} Value</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Customers:</span>
                      <span className="font-semibold">{data.count.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Avg CLV:</span>
                      <span className="font-semibold">KSh {data.avgCLV.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-sm text-gray-600">
              <p>Total customers analyzed: {clvData.customers?.length || 0}</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#d44100]"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-3xl font-bold text-gray-800">360° Analytics Dashboard</h2>
          <p className="text-sm text-gray-600 mt-1">Comprehensive e-commerce performance insights</p>
        </div>
        <div className="flex items-center gap-4">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#d44100]"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="365">Last year</option>
          </select>
          <button
            onClick={() => setRefreshKey(prev => prev + 1)}
            className="px-4 py-2 bg-[#d44100] text-white rounded-lg hover:bg-[#b83700] flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b mb-6">
        <div className="flex space-x-4">
          {[
            { id: 'revenue', label: 'Revenue & Sales', icon: DollarSign },
            { id: 'journey', label: 'Customer Journey', icon: Users },
            { id: 'marketing', label: 'Marketing', icon: BarChart3 },
            { id: 'products', label: 'Products', icon: Package },
            { id: 'operations', label: 'Operations', icon: Activity }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 border-b-2 transition-colors flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'border-[#d44100] text-[#d44100] font-semibold'
                  : 'border-transparent text-gray-600 hover:text-gray-800'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div>
        {activeTab === 'revenue' && renderRevenueIntelligence()}
        {activeTab === 'journey' && renderCustomerJourney()}
        {activeTab === 'marketing' && renderMarketingPerformance()}
        {activeTab === 'products' && renderProductPerformance()}
        {activeTab === 'operations' && renderOperationalEfficiency()}
      </div>
    </div>
  );
};

export default Advanced360Dashboard;

